import React, { useRef, useState } from 'react'
import { CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { DetailButton } from '../../../components/common/detail'
import { ActionImportSource } from '../../../types/actionImportTypes'
import { JiraState } from './useImportWizard'

export const MAX_FILE_BYTES = 10 * 1024 * 1024

const SOURCES: { id: ActionImportSource; ext?: string; mime: string[] }[] = [
  { id: 'csv', ext: '.csv', mime: ['text/csv', 'application/vnd.ms-excel', 'text/plain'] },
  { id: 'json', ext: '.json', mime: ['application/json', 'text/plain'] },
  { id: 'xml', ext: '.xml', mime: ['application/xml', 'text/xml', 'text/plain'] },
  { id: 'jira', mime: [] },
]

const ICONS: Record<ActionImportSource, React.ReactNode> = {
  csv: (
    <path d="M5 2.5h6.5L15 6v11.5H5zM11.5 2.5V6H15M7.5 10h5M7.5 13h5" strokeLinecap="round" strokeLinejoin="round" />
  ),
  json: (
    <path d="M7.5 3.5c-2 0-2 1.5-2 3s-1 2.5-2 2.5c1 0 2 1 2 2.5s0 3 2 3M12.5 3.5c2 0 2 1.5 2 3s1 2.5 2 2.5c-1 0-2 1-2 2.5s0 3-2 3" strokeLinecap="round" strokeLinejoin="round" />
  ),
  xml: <path d="M7 6l-4 4 4 4M13 6l4 4-4 4M11 4.5l-2 11" strokeLinecap="round" strokeLinejoin="round" />,
  jira: (
    <path d="M10 2.8l7.2 7.2L10 17.2 2.8 10zM10 7l3 3-3 3-3-3z" strokeLinejoin="round" />
  ),
}

/** Returns an i18n key when the file can't be sent to the extractor. */
export function validateImportFile(file: File, source: ActionImportSource): string | null {
  const spec = SOURCES.find((s) => s.id === source)
  if (!spec?.ext) return null
  if (!file.name.toLowerCase().endsWith(spec.ext)) return 'actionImport.source.errors.extension'
  if (file.size === 0) return 'actionImport.source.errors.empty'
  if (file.size > MAX_FILE_BYTES) return 'actionImport.source.errors.tooLarge'
  return null
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

type SourceStepProps = {
  source: ActionImportSource | null
  file: File | null
  jira: JiraState
  jiraIssueCount?: number
  onSelectSource: (source: ActionImportSource) => void
  onFile: (file: File | null) => void
  onConnectJira: () => void
}

export default function SourceStep({
  source,
  file,
  jira,
  jiraIssueCount,
  onSelectSource,
  onFile,
  onConnectJira,
}: SourceStepProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [dragging, setDragging] = useState(false)
  const [fileError, setFileError] = useState<string | null>(null)

  const spec = SOURCES.find((s) => s.id === source)

  const pickFile = (candidate: File | undefined | null) => {
    if (!candidate || !source) return
    const error = validateImportFile(candidate, source)
    setFileError(error)
    onFile(error ? null : candidate)
  }

  const handleCardKey = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    const moves: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }
    if (!(e.key in moves)) return
    e.preventDefault()
    const next = SOURCES[(index + moves[e.key] + SOURCES.length) % SOURCES.length]
    onSelectSource(next.id)
    setFileError(null)
    const el = document.getElementById(`action-import-source-${next.id}`)
    el?.focus()
  }

  return (
    <div className="action-import-source">
      <div
        className="action-import-source-cards"
        role="radiogroup"
        aria-label={t('actionImport.source.title')}
      >
        {SOURCES.map((s, index) => {
          const selected = source === s.id
          return (
            <div
              key={s.id}
              id={`action-import-source-${s.id}`}
              role="radio"
              aria-checked={selected}
              tabIndex={selected || (!source && index === 0) ? 0 : -1}
              className={`action-import-source-card${selected ? ' is-selected' : ''}`}
              onClick={() => {
                onSelectSource(s.id)
                setFileError(null)
              }}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault()
                  onSelectSource(s.id)
                  setFileError(null)
                } else handleCardKey(e, index)
              }}
            >
              <span className={`action-import-source-icon is-${s.id}`}>
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
                  {ICONS[s.id]}
                </svg>
              </span>
              <span className="action-import-source-name">{t(`actionImport.source.${s.id}.title`)}</span>
              <span className="action-import-source-desc">{t(`actionImport.source.${s.id}.desc`)}</span>
              {selected ? <span className="action-import-source-check" aria-hidden>✓</span> : null}
            </div>
          )
        })}
      </div>

      {!source ? (
        <div className="action-import-empty">{t('actionImport.source.pickSource')}</div>
      ) : source === 'jira' ? (
        <JiraPanel jira={jira} issueCount={jiraIssueCount} onConnect={onConnectJira} />
      ) : (
        <>
          <div
            className={`action-import-drop${dragging ? ' is-dragging' : ''}${file ? ' has-file' : ''}${fileError ? ' has-error' : ''}`}
            onDragOver={(e) => {
              e.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragging(false)
              pickFile(e.dataTransfer.files?.[0])
            }}
          >
            <input
              ref={inputRef}
              type="file"
              hidden
              accept={[spec?.ext, ...(spec?.mime ?? [])].filter(Boolean).join(',')}
              onChange={(e) => {
                pickFile(e.target.files?.[0])
                e.target.value = ''
              }}
            />
            {file ? (
              <div className="action-import-file">
                <span className={`action-import-source-icon is-${source}`}>
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
                    {ICONS[source]}
                  </svg>
                </span>
                <div className="action-import-file-meta">
                  <b>{file.name}</b>
                  <span>{formatSize(file.size)}</span>
                </div>
                <DetailButton variant="ghost" size="sm" onClick={() => inputRef.current?.click()}>
                  {t('actionImport.source.replace')}
                </DetailButton>
                <DetailButton variant="ghost" size="sm" onClick={() => onFile(null)}>
                  {t('actionImport.source.remove')}
                </DetailButton>
              </div>
            ) : (
              <>
                <svg className="action-import-drop-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
                  <path d="M12 15V4M7.5 8.5L12 4l4.5 4.5M4.5 15v3.5a1.5 1.5 0 001.5 1.5h12a1.5 1.5 0 001.5-1.5V15" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <p className="action-import-drop-title">
                  {t('actionImport.source.dropTitle', { ext: spec?.ext?.toUpperCase().slice(1) })}
                </p>
                <p className="action-import-drop-hint">
                  {t('actionImport.source.dropHint', { ext: spec?.ext })}
                </p>
                <DetailButton size="sm" onClick={() => inputRef.current?.click()}>
                  {t('actionImport.source.browse')}
                </DetailButton>
              </>
            )}
          </div>
          {fileError ? (
            <p className="action-import-field-error" role="alert">
              {t(fileError, { ext: spec?.ext, max: '10 MB' })}
            </p>
          ) : null}
          <p className="action-import-hint">{t(`actionImport.source.${source}.format`)}</p>
        </>
      )}
    </div>
  )
}

function JiraPanel({
  jira,
  issueCount,
  onConnect,
}: {
  jira: JiraState
  issueCount?: number
  onConnect: () => void
}) {
  const { t } = useTranslation()

  if (jira.status === 'connected') {
    return (
      <div className="action-import-jira is-connected" role="status">
        <span className="action-import-jira-badge">
          <span className="action-import-dot" aria-hidden />
          {t('actionImport.jira.connected')}
        </span>
        <div className="action-import-jira-grid">
          <div>
            <span>{t('actionImport.jira.project')}</span>
            <b>{t('actionImport.jira.projectConfigured')}</b>
          </div>
          <div>
            <span>{t('actionImport.jira.issues')}</span>
            <b>{issueCount ?? 0}</b>
          </div>
        </div>
        <p className="action-import-hint">{t('actionImport.jira.pickInReview')}</p>
        <DetailButton variant="ghost" size="sm" onClick={onConnect}>
          {t('actionImport.jira.refresh')}
        </DetailButton>
      </div>
    )
  }

  return (
    <div className="action-import-jira">
      <h4>{t('actionImport.jira.title')}</h4>
      <p>{t('actionImport.jira.desc')}</p>
      <ul className="action-import-jira-points">
        <li>{t('actionImport.jira.pointServer')}</li>
        <li>{t('actionImport.jira.pointLimit')}</li>
      </ul>
      {jira.status === 'error' ? (
        <p className="action-import-field-error" role="alert">
          {t('actionImport.jira.failed', { message: jira.error })}
        </p>
      ) : null}
      <DetailButton onClick={onConnect} disabled={jira.status === 'connecting'}>
        {jira.status === 'connecting' ? (
          <>
            <CircularProgress size={14} color="inherit" />
            {t('actionImport.jira.connecting')}
          </>
        ) : jira.status === 'error' ? (
          t('actionImport.jira.retry')
        ) : (
          t('actionImport.jira.connect')
        )}
      </DetailButton>
    </div>
  )
}
